import { useCallback, useEffect, useMemo, useState } from "react";
import { GraphQlAdapter } from "@src/infrastructure/adapters/graphQl/GraphQl.adapter";
import { GET_ACCOUNTING_SYSTEMS_LOOKUP } from "@src/domain/graphql";
import type {
  AccountingSystemLookupItem,
  AccountingSystemsLookupResponse,
} from "@src/domain/dto/accountingSystems";

export interface AccountingSystemsLookupSlice {
  accountingSystemsLookup: AccountingSystemLookupItem[] | null;
  accountingSystemsLookupCount: number;
  accountingSystemsLookupLoading: boolean;
  accountingSystemsLookupError: string | null;
  refetchAccountingSystemsLookup: () => Promise<void>;
}

export function useAccountingSystemsLookup(): AccountingSystemsLookupSlice {
  const [accountingSystemsLookup, setAccountingSystemsLookup] = useState<
    AccountingSystemLookupItem[] | null
  >(null);
  const [accountingSystemsLookupCount, setAccountingSystemsLookupCount] =
    useState(0);
  const [accountingSystemsLookupLoading, setAccountingSystemsLookupLoading] =
    useState(true);
  const [accountingSystemsLookupError, setAccountingSystemsLookupError] =
    useState<string | null>(null);

  const refetchAccountingSystemsLookup = useCallback(async () => {
    setAccountingSystemsLookupLoading(true);
    setAccountingSystemsLookupError(null);
    try {
      const client = GraphQlAdapter.getInstance().getClient();
      const result = await client.query<AccountingSystemsLookupResponse>({
        query: GET_ACCOUNTING_SYSTEMS_LOOKUP,
      });
      const data = result.data?.accountingSystemsLookup;
      if (data) {
        setAccountingSystemsLookup(data.items ?? null);
        setAccountingSystemsLookupCount(data.count ?? 0);
      } else {
        setAccountingSystemsLookup(null);
        setAccountingSystemsLookupCount(0);
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Failed to load accounting systems";
      setAccountingSystemsLookupError(message);
      setAccountingSystemsLookup(null);
      setAccountingSystemsLookupCount(0);
    } finally {
      setAccountingSystemsLookupLoading(false);
    }
  }, []);

  useEffect(() => {
    refetchAccountingSystemsLookup();
  }, [refetchAccountingSystemsLookup]);

  return useMemo(
    () => ({
      accountingSystemsLookup,
      accountingSystemsLookupCount,
      accountingSystemsLookupLoading,
      accountingSystemsLookupError,
      refetchAccountingSystemsLookup,
    }),
    [
      accountingSystemsLookup,
      accountingSystemsLookupCount,
      accountingSystemsLookupLoading,
      accountingSystemsLookupError,
      refetchAccountingSystemsLookup,
    ],
  );
}
